import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './context/AuthContext';
import { CountryProvider } from './context/CountryContext';
import { LanguageProvider } from './i18n/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Applications from './pages/Applications';
import Verifications from './pages/Verifications';
import Users from './pages/Users';
import Logs from './pages/Logs';
import Staff from './pages/Staff';
import Vehicles from './pages/Vehicles';
import FleetApprovals from './pages/FleetApprovals';
import Dashboard from './pages/Dashboard';
import AcceptInvite from './pages/AcceptInvite';
import Protocols from './pages/Protocols';
import DamageCases from './pages/DamageCases';
import CarshareRegistrations from './pages/CarshareRegistrations';
import AllDamageReports from './pages/AllDamageReports';
import SafetyReports from './pages/SafetyReports';
import RoomsAdmin from './pages/RoomsAdmin';
import ProviderApplications from './pages/ProviderApplications';
import CategoryConfig from './pages/CategoryConfig';
import VehicleOwnerRequirements from './pages/VehicleOwnerRequirements';
import RideOperations from './pages/RideOperations';
import ComingSoon from './pages/ComingSoon';
import RequireOwnerOrAdmin from './components/RequireOwnerOrAdmin';
import RequireCaseControl from './components/RequireCaseControl';
import AccommodationOverview from './pages/AccommodationOverview';
import AccommodationListings from './pages/AccommodationListings';
import AccommodationBookings from './pages/AccommodationBookings';
import AccommodationCalendar from './pages/AccommodationCalendar';
import AccommodationProviderApplications from './pages/AccommodationProviderApplications';
import FeesAndTaxes from './pages/FeesAndTaxes';
import ListingEditor from './pages/ListingEditor';
import MarketplaceCategories from './pages/MarketplaceCategories';
import CountriesSettings from './pages/CountriesSettings';
import DataQuality from './pages/DataQuality';
import CaseControl from './pages/CaseControl';
import CaseControlCurrent from './pages/CaseControlCurrent';
import CaseControlHistory from './pages/CaseControlHistory';
import CaseControlSettings from './pages/CaseControlSettings';
import SupportCase from './pages/SupportCase';
import MyTasks from './pages/MyTasks';
import MyAccount from './pages/MyAccount';
import ActivityLog from './pages/ActivityLog';
import SupportTickets from './pages/SupportTickets';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CountryProvider>
        <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* Applications — with status filter in URL */}
              <Route path="applications" element={<Navigate to="/applications/submitted" replace />} />
              {/* 'pending' was renamed to 'submitted' (PDF v2.1 status lifecycle) — keep old bookmarks working */}
              <Route path="applications/pending" element={<Navigate to="/applications/submitted" replace />} />
              <Route path="applications/:status" element={<Applications />} />

              {/* Verifications — with status filter in URL */}
              <Route path="verifications" element={<Navigate to="/verifications/pending" replace />} />
              <Route path="verifications/:status" element={<Verifications />} />

              {/* Users — with filter in URL */}
              <Route path="users" element={<Navigate to="/users/all" replace />} />
              <Route path="users/:filter" element={<Users />} />

              {/* Vehicles moderation (Milestone 45 — owners list review) */}
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="fleet-approvals" element={<FleetApprovals />} />
              <Route path="protocols" element={<Protocols />} />
              <Route path="damage-cases" element={<DamageCases />} />
              <Route path="all-damage-reports" element={<AllDamageReports />} />
              <Route path="carshare-damage-reports" element={<AllDamageReports />} />
              <Route path="carshare-registrations" element={<CarshareRegistrations />} />
              <Route path="safety-reports" element={<SafetyReports />} />
              {/* Old Rooms admin pages — kept reachable by direct URL for backward
                  compatibility, but no longer linked from the nav (replaced by
                  Accommodation/Providers below per the client's restructure spec). */}
              <Route path="rooms" element={<RoomsAdmin />} />
              <Route path="provider-applications" element={<ProviderApplications />} />
              <Route path="category-config" element={<CategoryConfig />} />
              <Route path="vehicle-owner-requirements" element={<VehicleOwnerRequirements />} />
              <Route path="ride-operations" element={<RideOperations />} />
              <Route path="marketplace-categories" element={<MarketplaceCategories />} />

              {/* Accommodation/Providers — replaces the old single "Rooms" nav item */}
              <Route path="accommodation" element={<Navigate to="/accommodation/overview" replace />} />
              <Route path="accommodation/overview" element={<AccommodationOverview />} />
              <Route path="accommodation/provider-applications" element={<ProviderApplications scope="accommodation" />} />
              <Route path="accommodation/hosts" element={<AccommodationProviderApplications />} />
              <Route path="accommodation/listings" element={<AccommodationListings />} />
              <Route path="accommodation/listings/:categorySlug" element={<AccommodationListings />} />
              <Route path="accommodation/calendar" element={<AccommodationCalendar />} />
              <Route path="accommodation/bookings" element={<AccommodationBookings />} />
              <Route path="accommodation/taxes" element={<Navigate to="/fees?tab=b" replace />} />
              <Route path="fees" element={<FeesAndTaxes />} />
              <Route path="accommodation/listings/edit/:id" element={<ListingEditor />} />
              <Route path="accommodation/reviews" element={<ComingSoon title="Reviews & Reports" description="Guest/host reviews moderation and reporting for Accommodation/Providers. Scheduled for a later phase." />} />

              {/* Logs — System section: Owner/Admin only, enforced here too, not just hidden from the nav */}
              {/* Old raw technical log, kept reachable by direct URL — not
                  linked from the nav anymore (replaced by the readable,
                  merged Activity Log below). */}
              <Route path="logs" element={<RequireOwnerOrAdmin><Logs /></RequireOwnerOrAdmin>} />
              <Route path="activity-log" element={<RequireOwnerOrAdmin><ActivityLog /></RequireOwnerOrAdmin>} />

              {/* Staff Management ("Team & Roles") — System section: Owner/Admin only */}
              <Route path="staff" element={<RequireOwnerOrAdmin><Staff /></RequireOwnerOrAdmin>} />

              {/* NULA MOVE · ADMIN navigation restructure — new sections, places
                  reserved in the nav now, real content in a later phase. */}
              <Route path="task-inbox" element={<MyTasks />} />
              <Route path="bookings-finance" element={<ComingSoon title="Bookings & Finance" description="Bookings & rides, Payments, Provider payouts, Commissions & pricing across USD/KES. Scheduled for Phase 4." />} />
              <Route path="support-tickets" element={<SupportTickets />} />
              <Route path="driver-safety-training" element={<ComingSoon title="Driver Safety Training" description="Training modules and completion tracking for drivers. Scheduled for a later phase." />} />
              <Route path="legal-texts" element={<ComingSoon title="Legal Texts & Templates" description="Terms, policies, and notice templates per country. Scheduled for a later phase." />} />
              <Route path="countries-settings" element={<RequireOwnerOrAdmin><CountriesSettings /></RequireOwnerOrAdmin>} />
              <Route path="data-quality" element={<RequireOwnerOrAdmin><DataQuality /></RequireOwnerOrAdmin>} />
              <Route path="case-control" element={<RequireCaseControl><CaseControl /></RequireCaseControl>} />
              <Route path="case-control/current" element={<CaseControlCurrent />} />
              <Route path="case-control/history" element={<CaseControlHistory />} />
              <Route path="case-control/support/:ticketId" element={<SupportCase />} />
              <Route path="case-control/settings" element={<RequireCaseControl><CaseControlSettings /></RequireCaseControl>} />
              <Route path="integrations" element={<RequireOwnerOrAdmin><ComingSoon title="Integrations & API" description="Third-party integrations and API credentials. Scheduled for Phase 4." /></RequireOwnerOrAdmin>} />
              <Route path="my-account" element={<MyAccount />} />
            </Route>
            <Route path="*" element={<Navigate to="/applications/submitted" replace />} />
          </Routes>
        </BrowserRouter>
        </LanguageProvider>
        </CountryProvider>
        <Toaster
          position="top-right"
          toastOptions={{ duration: 4000, style: { fontSize: 14 } }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}
